import { PropsWithChildren, useEffect, useState } from 'react';
import { Loader } from '@/components/Loader';
import { useAppointmentsStore } from '@/stores/appointments';
import { useChatsStore } from '@/stores/chats/chats.store';
import { useDocumentManagementStore } from '@/stores/documentManagement/documentManagement.store';
import { useInstitutionsStore } from '@/stores/institutions';
import { useAutoTimezone } from '@/hooks/useAutoTimezone';
import { useListStore } from '@/stores/list';
import { useNotificationsStore } from '@/stores/notifications';
import { useUsersStore } from '@/stores/users';
import { EUserRoles } from '@/types';
import { navigateToLogin } from '@/utils/navigateToLogin';
import { useSurveyStore } from '@/stores/survey';
import { useMedwandStore } from '@/stores/medwand';

export const PreloadQueriesLayout = ({ children }: PropsWithChildren) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingWithInstitutionId, setIsLoadingWithInstitutionId] =
    useState(true);
  const [isLoadingWithInstititonOptions, setIsLoadingWithInstitutionOptions] =
    useState(true);
  const { currentInstitution, fetchVisitNotesTemplates } = useInstitutionsStore(
    (state) => ({
      currentInstitution: state.currentInstitution,
      fetchVisitNotesTemplates: state.fetchVisitNotesTemplates,
    }),
  );
  const { fetchFormTemplates } = useDocumentManagementStore((state) => ({
    fetchFormTemplates: state.fetchFormTemplates,
  }));
  const { role, allRoles } = useUsersStore((state) => ({
    role: state.role,
    allRoles: state.allRoles,
  }));
  const { getAppointments, getAppointmentsAll } = useAppointmentsStore(
    (state) => ({
      getAppointments: state.getAppointments,
      getAppointmentsAll: state.getAppointmentsAll,
    }),
  );
  const { getCountries, getTimezones } = useListStore((state) => ({
    getCountries: state.getCountries,
    getTimezones: state.getTimezones,
  }));

  const fetchVitals = useMedwandStore(
    (medwandState) => medwandState.fetchVitals,
  );

  const getSpecialties = useSurveyStore((state) => state.getSpecialties);
  const isLoadingTimezones = useAutoTimezone();

  const getChats = useChatsStore((state) => state.getChats);

  const getNotifications = useNotificationsStore(
    (state) => state.getNotifications,
  );

  const getUsers = useUsersStore((state) => state.getUsers);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([getCountries(), getTimezones(), getSpecialties()]);
      } catch (error) {
        console.error('Failed to preload queries:', error);
        // loader is stuck for user if he is loged out during inital data loading
        // in that case isLoadingInstitutionId should be set to false
        setIsLoadingWithInstitutionId(false);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [getCountries, getTimezones, getSpecialties]);

  useEffect(() => {
    try {
      if (currentInstitution?.configuration?.medwand) void fetchVitals();
    } finally {
      setIsLoadingWithInstitutionOptions(false);
    }
  }, [currentInstitution?.configuration?.medwand, fetchVitals]);

  useEffect(() => {
    if (!currentInstitution?.id) return;
    const fetchData = async () => {
      if (!role) return;
      try {
        const getAppointmentsArgs = {
          institutionId: currentInstitution.id,
          role,
        };
        const queries: Promise<void>[] = [
          getUsers(currentInstitution.id),
          getNotifications(currentInstitution.id),
        ];

        // Check if user has ADMIN or PROVIDER among all their roles
        if (
          allRoles.includes(EUserRoles.ADMIN) ||
          allRoles.includes(EUserRoles.PROVIDER)
        ) {
          queries.push(fetchFormTemplates(currentInstitution.id));
        }

        if (
          allRoles.includes(EUserRoles.ADMIN) ||
          allRoles.includes(EUserRoles.PROVIDER) ||
          allRoles.includes(EUserRoles.COORDINATOR)
        ) {
          queries.push(fetchVisitNotesTemplates(currentInstitution.id));
        }
        await Promise.all(queries);

        // Load appointments and chats in the background: both stores load in
        // chunks and unblock their own `loading` after the first chunk, so we
        // don't hold the global loader while the remaining pages stream in.
        // The background promises handle their own errors since they are no
        // longer awaited by the try/catch above.
        const logBackgroundError = (error: unknown) => {
          console.error('Failed to preload queries:', error);
        };
        if ([EUserRoles.COORDINATOR, EUserRoles.PROVIDER].includes(role)) {
          getAppointmentsAll({ institutionId: currentInstitution.id }).catch(
            logBackgroundError,
          );
        } else if (![EUserRoles.ADMIN].includes(role)) {
          getAppointments(getAppointmentsArgs).catch(logBackgroundError);
        }

        getChats(currentInstitution.id).catch(logBackgroundError);
      } catch (error) {
        console.error('Failed to preload queries:', error);
      } finally {
        setIsLoadingWithInstitutionId(false);
      }
    };

    void fetchData();
  }, [
    currentInstitution?.id,
    fetchFormTemplates,
    fetchVisitNotesTemplates,
    getAppointments,
    getAppointmentsAll,
    getChats,
    getNotifications,
    getUsers,
    role,
    allRoles,
  ]);

  useEffect(() => {
    if (!currentInstitution?.id) return;
    const currentSubdomain = window.location.host.split('.')[0]?.toLowerCase();
    if (
      !currentInstitution?.whitelabel?.subdomain ||
      !currentInstitution?.whitelabel?.enabled
    ) {
      if (currentSubdomain === import.meta.env.VITE_DEFAULT_SUBDOMAIN) return;
      // reroute to "app" subdomain - the default one
      navigateToLogin({
        institutionId: currentInstitution.id,
        attachToken: true,
      });
      return;
    }
    if (
      currentSubdomain !== currentInstitution.whitelabel.subdomain.toLowerCase()
    ) {
      navigateToLogin({
        subdomain: currentInstitution.whitelabel.subdomain.toLowerCase(),
        institutionId: currentInstitution.id,
        attachToken: true,
      });
    }
  }, [currentInstitution]);

  if (
    isLoading ||
    isLoadingWithInstitutionId ||
    isLoadingWithInstititonOptions ||
    isLoadingTimezones
  )
    return <Loader full />;

  return children;
};
